"use client";

import { cn } from "@/lib/utils";
import { Badge } from "@/shadcn-components/ui/badge";
import {
  deriveAccountMetrics,
  useAccountInformationStore,
} from "@/vybe-trader-client/account-information";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols";
import { useSymbolsStore } from "@/vybe-trader-client/symbols";
import { useMemo } from "react";

const compactNumber = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "--";
  }

  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatAccountMoney = (
  value: number | null | undefined,
  currency?: string,
  digits = 2
) => {
  const formatted = compactNumber(value, digits);
  return currency && formatted !== "--" ? `${formatted} ${currency}` : formatted;
};

export const formatAccountPercent = (value: number | null | undefined) =>
  `${compactNumber(value, 2)}%`;

export function useAccountMetrics() {
  const account = useAccountInformationStore((state) => state.account);
  const trades = useOpenedTradesStore((state) => state.allTrades);
  const symbols = useSymbolsStore((state) => state.byCurrency);
  const quotes = useSubscribedSymbolsStore((state) => state.byCurrency);
  const currency = account?.currency || "USD";
  const derivedMetrics = useMemo(
    () =>
      deriveAccountMetrics({
        account,
        trades,
        symbols,
        quotes,
      }),
    [account, trades, symbols, quotes]
  );

  return {
    account,
    currency,
    profit: derivedMetrics.profit,
    derivedMetrics,
    metrics: [
      { label: "Balance", value: formatAccountMoney(account?.balance, currency) },
      {
        label: "Equity",
        value: formatAccountMoney(account ? derivedMetrics.equity : undefined, currency),
      },
      { label: "Credit", value: formatAccountMoney(account?.credit, currency) },
      {
        label: "Margin",
        value: formatAccountMoney(account ? derivedMetrics.margin : undefined, currency),
      },
      {
        label: "Free Margin",
        value: formatAccountMoney(account ? derivedMetrics.freeMargin : undefined, currency),
      },
      {
        label: "Margin Level (%)",
        value: formatAccountPercent(account ? derivedMetrics.marginLevel : undefined),
      },
    ],
  };
}

interface AccountMetricsRowsProps {
  className?: string;
  dense?: boolean;
  compact?: boolean;
}

export function AccountMetricsRows({
  className,
  dense = false,
  compact = false,
}: AccountMetricsRowsProps) {
  const { metrics } = useAccountMetrics();

  if (compact) {
    return (
      <div
        className={cn(
          "grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]",
          className,
        )}
      >
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="flex items-center justify-between gap-2 min-w-0"
          >
            <span className="truncate text-muted-foreground">{metric.label}</span>
            {metric.label === "Margin Level (%)" ? (
              <Badge
                variant="secondary"
                className="shrink-0 font-mono text-[10px] px-1.5 py-0"
              >
                {metric.value}
              </Badge>
            ) : (
              <span className="shrink-0 font-mono text-foreground tabular-nums">
                {metric.value}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn(dense ? "space-y-1" : "space-y-2", className)}>
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={cn(
            "flex items-center justify-between gap-4",
            dense ? "text-xs" : "text-sm"
          )}
        >
          <span className="text-muted-foreground">{metric.label}:</span>
          {metric.label === "Margin Level (%)" ? (
            <Badge variant="secondary" className="font-mono text-[11px]">
              {metric.value}
            </Badge>
          ) : (
            <span className="font-mono text-foreground tabular-nums">
              {metric.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

interface AccountMetricsStripProps {
  className?: string;
  /**
   * When true, the whole strip acts as a Tauri window drag handle: the root is a
   * drag region and its (read-only) contents are pointer-transparent so a mousedown
   * always resolves to the drag region instead of the text. Desktop shell only.
   */
  draggableRegion?: boolean;
}

export function AccountMetricsStrip({
  className,
  draggableRegion,
}: AccountMetricsStripProps) {
  const { metrics } = useAccountMetrics();

  return (
    <div
      data-tauri-drag-region={draggableRegion ? "" : undefined}
      className={cn(
        "hidden md:flex min-w-0 items-center gap-3 overflow-hidden text-[11px]",
        // Text passes clicks through to the drag region so the window can be moved.
        draggableRegion && "select-none [&_*]:pointer-events-none",
        className
      )}
    >
      {metrics.map((metric) => (
        <div key={metric.label} className="flex items-baseline gap-1 whitespace-nowrap">
          <span className="text-muted-foreground">{metric.label}</span>
          <span className="font-mono text-foreground tabular-nums">
            {metric.value}
          </span>
        </div>
      ))}
    </div>
  );
}
